import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Sparkles, RefreshCw } from 'lucide-react';
import DriftAlertCard from '@/components/DriftAlertCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const A_CFG = { under_reserved: { color: C.red, label: 'Under-reserved' }, over_reserved: { color: C.blue, label: 'Over-reserved' } };

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

export default function ReserveReviewPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(null);

  const load = useCallback(() => {
    api.get('/claims/reserving/review').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const apply = async (item) => {
    setApplying(item.claim_id);
    try {
      await api.post(`/claims/${item.claim_id}/update-reserve`, {
        new_reserve: item.recommended_reserve,
        reason: `ARIA smart reserve: 75th percentile of ${item.cohort_sample} settled ${item.claim_type} claims`,
      });
      toast.success(`${item.claim_reference} reserve set to ${fmt$(item.recommended_reserve)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update reserve'); }
    setApplying(null);
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="reserve-review-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Reserve Review</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>{data.method}. ARIA sweeps every open claim and flags reserves that drift from the book's real settlement history.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm text-xs" data-testid="reserve-review-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Re-sweep
        </Button>
      </div>

      <DriftAlertCard api={api} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['Open claims reviewed', data.reviewed, C.navy],
          ['Under-reserved', data.under_reserved, C.red],
          ['Over-reserved', data.over_reserved, C.blue],
          ['Adequate', data.adequate, C.green]].map(([l, v, color]) => (
          <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <Overline>{l}</Overline>
            <p className="text-3xl font-light tracking-tight mt-1" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{(v || 0).toLocaleString()}</p>
          </Card>
        ))}
      </div>

      <Card className="p-3 rounded-sm bg-white flex items-center gap-2" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
        <Sparkles className="w-4 h-4" style={{ color: C.teal }} />
        <p className="text-xs" style={{ color: C.slate }}>
          Net reserve adjustment if all recommendations were applied:{' '}
          <span className="font-semibold" style={{ color: data.net_gap > 0 ? C.red : C.green, fontVariantNumeric: 'tabular-nums' }} data-testid="reserve-review-netgap">
            {data.net_gap > 0 ? '+' : ''}{fmt$(data.net_gap)}
          </span>
          {data.no_history > 0 && <> · {data.no_history} large claim{data.no_history > 1 ? 's' : ''} skipped — no comparable settlement history, human judgment applies</>}
        </p>
      </Card>

      <Card className="rounded-sm bg-white overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="reserve-review-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.15)' }}>
              <th className="text-left py-2.5 px-4 font-semibold">Claim</th>
              <th className="text-left py-2.5 px-3 font-semibold">Type</th>
              <th className="text-left py-2.5 px-3 font-semibold">Assessment</th>
              <th className="text-right py-2.5 px-3 font-semibold">Current</th>
              <th className="text-right py-2.5 px-3 font-semibold">Recommended</th>
              <th className="text-right py-2.5 px-3 font-semibold">Gap</th>
              <th className="text-right py-2.5 px-4 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-sm" style={{ color: C.slate }}>Every open reserve sits within tolerance of the book's settlement history.</td></tr>
            )}
            {data.items.map(item => {
              const cfg = A_CFG[item.assessment];
              return (
                <tr key={item.claim_id} className="border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                  <td className="py-2.5 px-4">
                    <span className="font-mono text-xs" style={{ color: C.navy }}>{item.claim_reference}</span>
                    <p className="text-[10px]" style={{ color: C.slate }}>{item.business_name}</p>
                  </td>
                  <td className="py-2.5 px-3 text-xs capitalize" style={{ color: C.slate }}>{(item.claim_type || '').replace('_', ' ')}</td>
                  <td className="py-2.5 px-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${cfg.color}12`, color: cfg.color }}>{cfg.label}</span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs" style={{ color: C.navy }}>{fmt$(item.reserve_amount)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs font-semibold" style={{ color: C.navy }}>{fmt$(item.recommended_reserve)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs" style={{ color: item.gap > 0 ? C.red : C.blue }}>{item.gap > 0 ? '+' : ''}{fmt$(item.gap)}</td>
                  <td className="py-2.5 px-4 text-right">
                    <Button size="sm" disabled={applying === item.claim_id} onClick={() => apply(item)}
                      className="text-[11px] rounded-sm text-white h-7" style={{ background: cfg.color }} data-testid={`reserve-apply-${item.claim_reference}`}>
                      {applying === item.claim_id ? 'Applying…' : 'Apply'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
